import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LightBulbIcon,
  AdjustmentsHorizontalIcon,
  UserGroupIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export default function WhyChooseUs() {
  const { t } = useTranslation('home');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const reasons = [
    {
      titleKey: "whyChooseUs.reasons.expertise.title",
      descriptionKey: "whyChooseUs.reasons.expertise.description",
      Icon: LightBulbIcon,
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
    },
    {
      titleKey: "whyChooseUs.reasons.tailored.title",
      descriptionKey: "whyChooseUs.reasons.tailored.description",
      Icon: AdjustmentsHorizontalIcon,
      color: "text-primary",
      bgColor: "bg-primary/5",
      borderColor: "border-primary/20",
    },
    {
      titleKey: "whyChooseUs.reasons.customerCentric.title",
      descriptionKey: "whyChooseUs.reasons.customerCentric.description",
      Icon: UserGroupIcon,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
    },
    {
      titleKey: "whyChooseUs.reasons.timely.title",
      descriptionKey: "whyChooseUs.reasons.timely.description",
      Icon: ClockIcon,
      color: "text-green-500",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    },
  ];

  return (
    <div
      ref={sectionRef}
      id="why-choose-us"
      className="py-24 px-6 sm:px-12 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto">
        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            {t('whyChooseUs.badge')}
          </span>
          <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            {t('whyChooseUs.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">{t('whyChooseUs.titleHighlight')}</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t('whyChooseUs.description')}
          </p>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 max-w-5xl mx-auto">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className={`group relative flex items-start gap-5 p-6 bg-white rounded-2xl border-2 ${reason.borderColor} hover:border-primary/40 transition-all duration-500 hover:shadow-lg hover:-translate-y-1`}
              style={{
                transitionDelay: `${index * 100}ms`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              }}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-14 h-14 ${reason.bgColor} ${reason.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <reason.Icon className="w-7 h-7" />
              </div>

              {/* Content */}
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors duration-300">
                  {t(reason.titleKey)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(reason.descriptionKey)}
                </p>
              </div>

              {/* Number indicator */}
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-400 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
